// web/src/components/customer/CompBanner.tsx
//
// Top-of-page banner showing pending comp(s) for the signed-in customer.
// Reads from comp_offers via the supabase client (RLS allows customer
// self-read via the comp_offers_customer_read policy from migration 013).

import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface CompRow {
  id: string;
  code: string;
  shop_id: string;
  expires_at: string;
  shop_name?: string;
}

interface Props {
  customerAddress: string;
}

export function CompBanner({ customerAddress }: Props) {
  const [comps, setComps] = useState<CompRow[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('comp_offers')
        .select('id, code, shop_id, expires_at, shops(name)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (cancelled || error) return;
      setComps((data || []).map((r: any) => ({
        id: r.id,
        code: r.code,
        shop_id: r.shop_id,
        expires_at: r.expires_at,
        shop_name: r.shops?.name,
      })));
    })();
    return () => { cancelled = true; };
  }, [customerAddress]);

  const visible = comps.filter((c) => !dismissed.has(c.id));
  if (visible.length === 0) return null;

  return (
    <div role="region" aria-label="Free coffee offers" className="space-y-2 mb-4">
      {visible.map((c) => (
        <div
          key={c.id}
          className="bg-white rounded-[10px] border-2 border-coral p-4 flex items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="font-heading text-[8px] text-coral tracking-[1px] mb-1">
              FREE COFFEE FROM {c.shop_name || 'A SHOP'}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <code className="font-grotesk font-bold text-lg text-primary tracking-wider">{c.code}</code>
              <a
                className="font-grotesk font-bold text-[12px] text-coral underline"
                href={`/redeem-comp?code=${encodeURIComponent(c.code)}`}
              >
                SHOW CODE →
              </a>
            </div>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissed((s) => new Set(s).add(c.id))}
            className="font-grotesk text-body-grey hover:text-primary text-lg leading-none px-1"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
